import Cookies from "js-cookie";

/**
 * Utility function to get the authentication token from various sources
 * Uses the same logic as the API interceptor for consistency
 * @returns {string|null} The authentication token or null if not found
 */
export const getAuthToken = () => {
  // Match the exact logic from api.js interceptor
  const token = Cookies.get("token") || localStorage.getItem("accessToken");

  // Ensure we don't return null-like strings
  if (!token || token === "null" || token === "undefined") {
    return null;
  }

  return token;
};

/**
 * Utility function to create authentication headers
 * @returns {object} Headers object with Authorization bearer token
 */
export const getAuthHeaders = () => {
  const token = getAuthToken();

  if (!token) {
    console.warn("No authentication token found");
    return {};
  }

  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
};

/**
 * Utility function to create multipart form headers with auth
 * @returns {object} Headers object with Authorization bearer token (no Content-Type for multipart)
 */
export const getAuthHeadersMultipart = () => {
  const token = getAuthToken();

  if (!token) {
    console.warn("No authentication token found");
    return {};
  }

  return {
    Authorization: `Bearer ${token}`,
    // Don't set Content-Type for multipart/form-data - browser sets it automatically
  };
};

/**
 * Utility function to sync token between cookies and localStorage
 * Helpful for debugging authentication issues
 */
export const syncAuthTokens = () => {
  const cookieToken = Cookies.get("token");
  const localToken = localStorage.getItem("accessToken");

  if (cookieToken && !localToken) {
    // Copy from cookie to localStorage
    localStorage.setItem("accessToken", cookieToken);
    console.log("Synced token from cookie to localStorage");
  } else if (!cookieToken && localToken) {
    // Copy from localStorage to cookie
    Cookies.set("token", localToken, { expires: 7 });
    console.log("Synced token from localStorage to cookie");
  }

  return getAuthToken();
};

/**
 * Debug function to log all token sources
 */
export const debugAuthTokens = () => {
  const cookieToken = Cookies.get("token");
  const accessToken = localStorage.getItem("accessToken");
  const storageToken = localStorage.getItem("token");
  const finalToken = getAuthToken();

  console.group("🔐 Auth Token Debug");
  console.log(
    "Cookie token:",
    cookieToken ? `${cookieToken.substring(0, 20)}...` : "None"
  );
  console.log(
    "AccessToken (localStorage):",
    accessToken ? `${accessToken.substring(0, 20)}...` : "None"
  );
  console.log(
    "Token (localStorage):",
    storageToken ? `${storageToken.substring(0, 20)}...` : "None"
  );
  console.log(
    "Final token (getAuthToken):",
    finalToken ? `${finalToken.substring(0, 20)}...` : "None"
  );
  console.log("Is valid:", !!finalToken && finalToken !== "null");
  console.groupEnd();

  return { cookieToken, accessToken, storageToken, finalToken };
};

export default {
  getAuthToken,
  getAuthHeaders,
  getAuthHeadersMultipart,
  syncAuthTokens,
  debugAuthTokens,
};
